/**
 * TURN-TAKING FROM THE MICROPHONE.
 *
 * Decides, from a stream of loudness samples, when a speaker has finished a
 * turn — so the mic can close and send without the user tapping anything.
 *
 * Deliberately knows nothing about Web Audio, MediaRecorder, or React. It is
 * fed `(rms, timestampMs)` pairs and answers one of three verdicts, which
 * makes every rule in it testable by pushing numbers at it. The Web Audio
 * plumbing that produces those numbers lives in `mic-level.ts`.
 *
 * ── THE ARMED RULE IS THE WHOLE SAFETY PROPERTY ─────────────────────────────
 * A turn can only END if it was ever seen to START. `send` is unreachable
 * until `armMs` of speech has accumulated, so a mic opened in a quiet cab and
 * left alone produces `waiting` forever and then `cap` — never `send`. That is
 * what keeps near-silence away from Whisper, which hallucinates filler on it
 * ("Thanks for watching") and would otherwise mint a message nobody spoke.
 *
 * The caller reads `armed` at `cap` to decide between sending and discarding.
 *
 * ── WHY THE ROOM IS MEASURED, AND WHY IT IS THE QUIETEST SAMPLE ─────────────
 * A truck idling beside the user sits well above any single loudness number
 * that would work in an office, so the first `calibrateMs` is spent measuring
 * the room rather than judging it, and the threshold is set from what was
 * measured.
 *
 * That opens an obvious failure: a user who starts talking the instant the mic
 * opens has their own voice measured as the room, and the threshold lands
 * above everything they say afterwards — a detector whose only possible answer
 * is `waiting`, for the rest of the session, with nothing on screen to say so.
 * An instrument whose range collapses to one value is not an instrument.
 *
 * THE QUIETEST SAMPLE IN THE WINDOW IS WHAT SEPARATES THE TWO CASES, and an
 * average cannot: the average of 300ms of engine noise and the average of
 * 300ms of a person talking are both simply "loud". Speech has gaps between
 * syllables and a running engine does not, so the FLOOR of the window is near
 * the room level in both cases, while the mean is only near it in one. Reading
 * the floor is what lets `maxRms` be set high enough to be useful in a loud
 * cab instead of having to double as the guard against this.
 *
 * `maxRms` still earns its place as the last stop for the pathological case —
 * one unbroken shout across the whole window — but it is no longer the only
 * thing standing between a talkative user and a deaf detector.
 */

export type TurnVerdict =
  /** Nothing has been decided yet. Keep recording. */
  | 'waiting'
  /** A turn started and has now ended. Send what was recorded. */
  | 'send'
  /** The hard ceiling was reached. Read `armed` to choose send or discard. */
  | 'cap';

export interface TurnDetectorConfig {
  /** Speech, in ms, needed before a turn counts as started. */
  armMs: number;
  /** Quiet, in ms, after a started turn, that ends it. */
  silenceMs: number;
  /** Ceiling on one turn, in ms. */
  maxMs: number;
  /** Opening window, in ms, spent measuring the room instead of judging it. */
  calibrateMs: number;
  /** Threshold is this many times the measured room level. */
  calibrateMultiplier: number;
  /** Threshold never drops under this — a truly quiet room must not arm on hiss. */
  minRms: number;
  /** Threshold never rises over this — see the note about a loud opening. */
  maxRms: number;
}

/**
 * Ordinary hands-free listening: the user is holding the phone and talking to
 * it, and the mic is theirs alone.
 */
export const HANDS_FREE_TURN: TurnDetectorConfig = {
  armMs: 400,
  silenceMs: 1600,
  maxMs: 30_000,
  calibrateMs: 300,
  calibrateMultiplier: 2.5,
  minRms: 0.02,
  maxRms: 0.35,
};

/**
 * Listening WHILE THE AGENT IS TALKING, for barge-in.
 *
 * Every number here is harder to satisfy than its sibling above, because the
 * loudest thing in the room is the agent's own read-back coming out of the
 * same phone. Echo cancellation is asked for on the capture, but on a phone
 * speaker it is not something this file can rely on, so the thresholds carry
 * the weight instead.
 *
 * `silenceMs` and `maxMs` never come into play: the caller watches `armed` and
 * acts the moment it goes true, then hands the turn to a detector built from
 * HANDS_FREE_TURN.
 */
export const BARGE_IN_TURN: TurnDetectorConfig = {
  armMs: 500,
  silenceMs: Number.POSITIVE_INFINITY,
  maxMs: Number.POSITIVE_INFINITY,
  calibrateMs: 400,
  calibrateMultiplier: 4,
  /*
   * Twice the ordinary floor. The agent's own voice arriving back through the
   * mic has to land UNDER this or the agent interrupts itself, and how loud
   * that leak actually is depends on the handset, the volume and whether the
   * user is on speaker — none of which this file can know. It is the number
   * the operator will move if barge-in misbehaves, and the reason the feature
   * ships off.
   */
  minRms: 0.12,
  maxRms: 0.4,
};

export class TurnDetector {
  private readonly cfg: TurnDetectorConfig;
  private startedAt: number | null = null;
  private lastAt = 0;
  private roomFloor = 0;
  private roomCount = 0;
  private thresholdValue: number;
  private speechMs = 0;
  private quietMs = 0;
  private armedValue = false;
  private smoothed = 0;
  private settled = false;

  constructor(cfg: TurnDetectorConfig) {
    this.cfg = cfg;
    this.thresholdValue = cfg.minRms;
  }

  /** True once enough speech has been heard for this turn to be sendable. */
  get armed(): boolean {
    return this.armedValue;
  }

  /** The loudness the room is being judged against. Exposed for the meter. */
  get threshold(): number {
    return this.thresholdValue;
  }

  /**
   * A smoothed 0-1 reading for the on-screen level meter.
   *
   * Scaled against the threshold rather than against 1.0, so the meter moves
   * the same amount for the same *speech* in a loud cab and a quiet office.
   * A bar that barely twitches is indistinguishable from a dead mic.
   */
  get meter(): number {
    const ceiling = Math.max(this.thresholdValue, this.cfg.minRms) * 4;
    return Math.min(1, this.smoothed / ceiling);
  }

  /**
   * Feed one loudness reading.
   *
   * `now` is a millisecond clock — elapsed time is taken from the samples
   * themselves rather than from a sample count, so a browser that throttles
   * the sampling timer does not silently stretch every window in here.
   */
  push(rms: number, now: number): TurnVerdict {
    this.smoothed = this.smoothed * 0.7 + rms * 0.3;

    if (this.startedAt === null) {
      this.startedAt = now;
      this.lastAt = now;
    }

    if (this.settled) return 'waiting';

    // A single delayed sample must not be counted as a second of speech or a
    // second of quiet, so the step between samples is bounded.
    const dt = Math.min(Math.max(now - this.lastAt, 0), 250);
    this.lastAt = now;
    const elapsed = now - this.startedAt;

    if (elapsed < this.cfg.calibrateMs) {
      // The QUIETEST sample, not the average — see the note at the top.
      this.roomFloor = this.roomCount === 0 ? rms : Math.min(this.roomFloor, rms);
      this.roomCount++;
      this.thresholdValue = Math.min(
        Math.max(this.roomFloor * this.cfg.calibrateMultiplier, this.cfg.minRms),
        this.cfg.maxRms,
      );
      return 'waiting';
    }

    if (elapsed >= this.cfg.maxMs) {
      this.settled = true;
      return 'cap';
    }

    if (rms > this.thresholdValue) {
      this.speechMs += dt;
      this.quietMs = 0;
      if (this.speechMs >= this.cfg.armMs) this.armedValue = true;
      return 'waiting';
    }

    if (this.armedValue) {
      this.quietMs += dt;
      if (this.quietMs >= this.cfg.silenceMs) {
        this.settled = true;
        return 'send';
      }
      return 'waiting';
    }

    // Not armed yet: let a scattered knock or door slam decay instead of
    // accumulating toward an arm it never earned.
    this.speechMs = Math.max(0, this.speechMs - dt);
    return 'waiting';
  }
}
